import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { LatLng, Product, ProductOptionGroup } from '@kick/types';

/** Opción elegida, con su nombre y precio congelados al momento de agregar. */
export interface OpcionElegida {
  groupId: string;
  groupName: string;
  optionId: string;
  optionName: string;
  priceDelta: number;
}

export interface LineaCarrito {
  /** Identifica la línea: el mismo producto con distintas opciones son líneas distintas. */
  key: string;
  productId: string;
  name: string;
  imageUrl: string | null;
  /** Precio base del producto, en centavos. */
  unitPrice: number;
  quantity: number;
  opciones: OpcionElegida[];
  notas: string;
}

export interface ComercioCarrito {
  id: string;
  slug: string;
  name: string;
  logoUrl: string | null;
  minOrderAmount: number;
  avgPrepMinutes: number;
  /**
   * Punto de recogida. Se guarda en el carrito para poder estimar el
   * domicilio sin volver a pedir el comercio a la API.
   */
  location: LatLng;
}

interface EstadoCarrito {
  /**
   * El carrito pertenece a un solo comercio.
   *
   * Un pedido se prepara y se recoge en un local: mezclar productos de dos
   * comercios exigiría dos preparaciones, dos recogidas y dos domicilios.
   */
  comercio: ComercioCarrito | null;
  lineas: LineaCarrito[];

  agregar: (
    comercio: ComercioCarrito,
    producto: Product,
    opciones: OpcionElegida[],
    cantidad: number,
    notas: string,
  ) => void;
  cambiarCantidad: (key: string, cantidad: number) => void;
  quitar: (key: string) => void;
  vaciar: () => void;
  /** ¿Agregar de este comercio obligaría a descartar lo que ya hay? */
  requiereCambiarComercio: (merchantId: string) => boolean;
}

/** Clave estable: mismo producto y mismas opciones ⇒ misma línea. */
function claveLinea(productId: string, opciones: OpcionElegida[], notas: string): string {
  const ids = opciones
    .map((o) => o.optionId)
    .sort()
    .join(',');
  return `${productId}|${ids}|${notas.trim().toLowerCase()}`;
}

export const useCarrito = create<EstadoCarrito>()(
  persist(
    (set, get) => ({
      comercio: null,
      lineas: [],

      agregar: (comercio, producto, opciones, cantidad, notas) => {
        const estado = get();
        // Cambiar de comercio descarta el carrito anterior: la pantalla
        // pregunta antes de llegar aquí.
        const lineasBase = estado.comercio?.id === comercio.id ? estado.lineas : [];
        const key = claveLinea(producto.id, opciones, notas);
        const existente = lineasBase.find((l) => l.key === key);

        set({
          comercio,
          lineas: existente
            ? lineasBase.map((l) =>
                l.key === key ? { ...l, quantity: Math.min(50, l.quantity + cantidad) } : l,
              )
            : [
                ...lineasBase,
                {
                  key,
                  productId: producto.id,
                  name: producto.name,
                  imageUrl: producto.imageUrl,
                  unitPrice: producto.price,
                  quantity: cantidad,
                  opciones,
                  notas: notas.trim(),
                },
              ],
        });
      },

      cambiarCantidad: (key, cantidad) => {
        if (cantidad <= 0) return get().quitar(key);
        set({
          lineas: get().lineas.map((l) =>
            l.key === key ? { ...l, quantity: Math.min(50, cantidad) } : l,
          ),
        });
      },

      quitar: (key) => {
        const lineas = get().lineas.filter((l) => l.key !== key);
        // Sin líneas no hay comercio: así el próximo producto no arrastra el anterior.
        set({ lineas, comercio: lineas.length > 0 ? get().comercio : null });
      },

      vaciar: () => set({ comercio: null, lineas: [] }),

      requiereCambiarComercio: (merchantId) => {
        const { comercio, lineas } = get();
        return lineas.length > 0 && !!comercio && comercio.id !== merchantId;
      },
    }),
    {
      name: 'kick-carrito',
      // Solo el contenido: las funciones se reconstruyen al cargar.
      partialize: (estado) => ({ comercio: estado.comercio, lineas: estado.lineas }),
    },
  ),
);

// ── Cálculos derivados ──────────────────────────────────────

/** Precio de una línea incluyendo sus opciones, por la cantidad. */
export function totalLinea(linea: LineaCarrito): number {
  const extras = linea.opciones.reduce((suma, o) => suma + o.priceDelta, 0);
  return (linea.unitPrice + extras) * linea.quantity;
}

export function subtotalCarrito(lineas: LineaCarrito[]): number {
  return lineas.reduce((suma, l) => suma + totalLinea(l), 0);
}

export function cantidadTotal(lineas: LineaCarrito[]): number {
  return lineas.reduce((suma, l) => suma + l.quantity, 0);
}

// ── Validación de modificadores ─────────────────────────────

export interface ErrorSeleccion {
  groupId: string;
  mensaje: string;
}

/**
 * Comprueba que la selección cumple las reglas de cada grupo.
 * Se valida aquí y en el servidor: aquí para guiar al cliente, allá porque
 * el navegador nunca es de fiar.
 */
export function validarSeleccion(
  grupos: ProductOptionGroup[],
  elegidas: OpcionElegida[],
): ErrorSeleccion[] {
  const errores: ErrorSeleccion[] = [];

  for (const grupo of grupos) {
    const cuantas = elegidas.filter((o) => o.groupId === grupo.id).length;

    if (grupo.isRequired && cuantas < Math.max(1, grupo.minSelect)) {
      errores.push({
        groupId: grupo.id,
        mensaje:
          grupo.minSelect > 1
            ? `Elige al menos ${grupo.minSelect} opciones`
            : 'Elige una opción',
      });
      continue;
    }

    if (cuantas > 0 && cuantas < grupo.minSelect) {
      errores.push({ groupId: grupo.id, mensaje: `Elige al menos ${grupo.minSelect} opciones` });
    }

    if (cuantas > grupo.maxSelect) {
      errores.push({ groupId: grupo.id, mensaje: `Máximo ${grupo.maxSelect} opciones` });
    }
  }

  return errores;
}
